/* REASON RetailOS™ — Theme + Accessibility + i18n provider stack.
   Phase 0A. Persists everything to localStorage and syncs to the backend
   (/api/design/preferences) when the user is signed in. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { applyBrandPrimaryRaw } from "@/lib/brandColor";

const STORAGE_KEY = "reason_ux_prefs_v1";

const DEFAULTS = {
  theme: "system",            // light | dark | high_contrast | system
  language: "en",             // en | hi | ar | bn | ta | te | ...
  density: "comfortable",     // comfortable | compact
  reduced_motion: false,
  high_contrast: false,
  dyslexia_mode: false,
  font_scale: 1,              // 0.875 .. 1.5
  keyboard_shortcuts: true,
  color_blind_mode: "off",    // off | protanopia | deuteranopia | tritanopia
  accent: "default",          // "default" | HSL triplet e.g. "24 95% 53%"
};

const RTL_LANGS = new Set(["ar"]);

const UxContext = createContext(null);

function readLS() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

function applyToHtml(p) {
  const root = document.documentElement;
  // Resolve theme (system -> match media)
  const resolved =
    p.theme === "system"
      ? window.matchMedia?.("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : p.theme;

  root.classList.remove("dark", "hc");
  if (resolved === "dark") root.classList.add("dark");
  if (resolved === "high_contrast" || p.high_contrast) root.classList.add("hc");

  root.classList.toggle("reduced-motion", !!p.reduced_motion);
  root.classList.toggle("dyslexia", !!p.dyslexia_mode);
  root.classList.toggle("density-compact", p.density === "compact");
  root.setAttribute("data-color-blind", p.color_blind_mode || "off");
  root.style.setProperty("--font-scale", String(p.font_scale || 1));
  // Accent color override (per-user). "default" clears the override so tenant/theme brand shows.
  if (p.accent && p.accent !== "default") {
    root.style.setProperty("--primary", p.accent);
    root.style.setProperty("--ring", p.accent);
    root.style.setProperty("--sidebar-primary", p.accent);
  } else {
    // accent = default → hand --primary back to the tenant brand color (if any), else CSS default.
    root.style.removeProperty("--sidebar-primary");
    let brandHex = null;
    try { brandHex = (JSON.parse(localStorage.getItem("reason_login_brand") || "{}") || {}).primary_color || null; } catch { brandHex = null; }
    if (brandHex) { applyBrandPrimaryRaw(brandHex); } else {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
    }
  }
  root.setAttribute("lang", p.language);
  root.setAttribute("dir", RTL_LANGS.has(p.language) ? "rtl" : "ltr");
}

export function UxProvider({ children }) {
  const [prefs, setPrefs] = useState(() => readLS());
  const [translations, setTranslations] = useState({});

  // Apply on every change
  useEffect(() => {
    applyToHtml(prefs);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch {}
  }, [prefs]);

  // Listen for OS dark-mode flips when theme=system
  useEffect(() => {
    if (prefs.theme !== "system") return;
    const m = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!m) return;
    const fn = () => applyToHtml({ ...prefs });
    m.addEventListener?.("change", fn);
    return () => m.removeEventListener?.("change", fn);
  }, [prefs]);

  // Load translations for current language
  useEffect(() => {
    let alive = true;
    api.get(`/design/translations/${prefs.language}`)
      .then((r) => { if (alive) setTranslations(r.data || {}); })
      .catch(() => { if (alive) setTranslations({}); });
    return () => { alive = false; };
  }, [prefs.language]);

  // Pull server prefs once on mount (best-effort)
  useEffect(() => {
    // Only hit /design/preferences when signed in (a token exists). Anonymous visitors on public
    // pages have no server prefs — probing would just 401 and spam the console. Re-fetch on the
    // "reason-auth-changed" event so a same-tab sign-in still hydrates prefs without a reload.
    const loadPrefs = () => {
      if (!localStorage.getItem("reason_token")) return;
      api.get("/design/preferences").then((r) => {
        const server = r.data || {};
        // server values overwrite local only if they're explicitly set (truthy/numeric)
        const merged = { ...prefs };
        let touched = false;
        for (const k of Object.keys(DEFAULTS)) {
          if (server[k] !== undefined && server[k] !== null && server[k] !== "") {
            if (merged[k] !== server[k]) { merged[k] = server[k]; touched = true; }
          }
        }
        if (touched) setPrefs(merged);
      }).catch(() => {});
    };
    loadPrefs();
    window.addEventListener("reason-auth-changed", loadPrefs);
    return () => window.removeEventListener("reason-auth-changed", loadPrefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((patch) => {
    setPrefs((p) => {
      const next = { ...p, ...patch };
      api.put("/design/preferences", patch).catch(() => {});
      return next;
    });
  }, []);

  const t = useCallback((key, fallback) => translations[key] || fallback || key, [translations]);

  const value = useMemo(() => ({
    prefs, update, t,
    isRTL: RTL_LANGS.has(prefs.language),
    resolvedTheme:
      prefs.theme === "system"
        ? (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light")
        : prefs.theme,
  }), [prefs, update, t]);

  return <UxContext.Provider value={value}>{children}</UxContext.Provider>;
}

export function useUx() {
  const ctx = useContext(UxContext);
  if (!ctx) throw new Error("useUx must be inside UxProvider");
  return ctx;
}

export function useT() { return useUx().t; }
